<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

// Shopee return/refund action card. Chatwoot is UI only — actions go through
// the Rails proxy (mh_returns_controller) to the conversation's owning company
// backend, which makes the signed marketplace call. Approve/Reject commit real
// money — human taps only, with an explicit confirm step.

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const accountId = useMapGetter('getCurrentAccountId');

const returns = ref([]);
const isShopee = ref(false);
const busySn = ref('');
const actionError = ref('');
const rejectOpenFor = ref('');
const disputeReasons = ref([]);
const rejectForm = ref({ reason: '', email: '', text: '' });
const collapsed = ref(false);

const base = computed(
  () => `/api/v1/accounts/${accountId.value}/mh_returns/${props.chat?.id}`
);

// Statuses where seller action is possible (per v2.returns docs: dispute is
// allowed while REQUESTED/PROCESSING/ACCEPTED; confirm acts on the request).
const canApprove = r => r.status === 'REQUESTED';
const canReject = r =>
  ['REQUESTED', 'PROCESSING', 'ACCEPTED'].includes(r.status);

const STATUS_STYLE = {
  REQUESTED: 'bg-y-warning-3 text-y-warning-11',
  PROCESSING: 'bg-b-info-3 text-b-info-11',
  ACCEPTED: 'bg-g-success-3 text-g-success-11',
  JUDGING: 'bg-p-primary-3 text-p-primary-11',
  SELLER_DISPUTE: 'bg-p-primary-3 text-p-primary-11',
};
const statusClass = s => STATUS_STYLE[s] || 'bg-n-alpha-2 text-n-slate-11';

const visibleReturns = computed(() => returns.value.slice(0, 5));

function fmtAmount(r) {
  if (r.refund_amount == null) return '';
  return `${r.currency || 'MYR'} ${Number(r.refund_amount).toFixed(2)}`;
}

function fmtDue(r) {
  if (!r.due_date) return '';
  const d = new Date(r.due_date * 1000);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  const when = d.toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
  });
  return days >= 0
    ? `respond by ${when} (${days}d)`
    : `due date passed (${when})`;
}

async function fetchReturns() {
  if (!props.chat?.id) return;
  actionError.value = '';
  try {
    const { data } = await window.axios.get(`${base.value}/by_buyer`);
    isShopee.value = Boolean(data.shopee);
    returns.value = Array.isArray(data.return) ? data.return : [];
  } catch (e) {
    // Non-shopee inboxes and transient backend errors both end silent:
    // this card must never block the conversation view.
    isShopee.value = false;
    returns.value = [];
  }
}

async function approve(r) {
  actionError.value = '';
  const ok = window.confirm(
    `APPROVE return/refund ${r.return_sn}?\n\nThis accepts the ${fmtAmount(r)} refund for "${(r.text_reason || r.reason || '').slice(0, 80)}" and cannot be undone.`
  );
  if (!ok) return;
  busySn.value = r.return_sn;
  try {
    await window.axios.post(`${base.value}/confirm/${r.return_sn}`);
    await fetchReturns();
  } catch (e) {
    actionError.value =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      'Approve failed';
  } finally {
    busySn.value = '';
  }
}

async function openReject(r) {
  actionError.value = '';
  rejectForm.value = { reason: '', email: '', text: '' };
  rejectOpenFor.value = r.return_sn;
  disputeReasons.value = [];
  try {
    const { data } = await window.axios.get(
      `${base.value}/dispute_reasons/${r.return_sn}`
    );
    disputeReasons.value = data.dispute_reason || [];
  } catch (e) {
    actionError.value = 'Could not load dispute reasons';
  }
}

async function submitReject(r) {
  if (!rejectForm.value.reason || !rejectForm.value.email) return;
  busySn.value = r.return_sn;
  actionError.value = '';
  try {
    await window.axios.post(`${base.value}/dispute/${r.return_sn}`, {
      email: rejectForm.value.email,
      dispute_reason: rejectForm.value.reason,
      dispute_text_reason: rejectForm.value.text || undefined,
    });
    rejectOpenFor.value = '';
    await fetchReturns();
  } catch (e) {
    actionError.value =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      'Dispute failed';
  } finally {
    busySn.value = '';
  }
}

watch(
  () => props.chat?.id,
  () => {
    returns.value = [];
    rejectOpenFor.value = '';
    fetchReturns();
  }
);
onMounted(fetchReturns);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <!-- Internal MakerHub CS tool — English-only strings by design (fork precedent). -->
  <div
    v-if="isShopee && returns.length"
    class="mx-3 mt-1 rounded-lg border border-n-weak bg-n-background"
  >
    <div
      class="flex items-center gap-2 px-4 py-2 cursor-pointer select-none"
      @click="collapsed = !collapsed"
    >
      <fluent-icon icon="arrow-swap" size="16" class="text-y-warning-11" />
      <span class="text-sm font-medium text-n-slate-12">
        Return/Refund — {{ returns.length }} request{{
          returns.length > 1 ? 's' : ''
        }}
      </span>
      <button
        class="ml-auto p-1 rounded hover:bg-n-alpha-2 text-n-slate-10"
        title="Refresh"
        @click.stop="fetchReturns"
      >
        <fluent-icon icon="arrow-clockwise" size="14" />
      </button>
      <fluent-icon
        :icon="collapsed ? 'chevron-down' : 'chevron-up'"
        size="14"
        class="text-n-slate-10"
      />
    </div>

    <div v-if="!collapsed" class="px-4 pb-3 flex flex-col gap-2">
      <div
        v-for="r in visibleReturns"
        :key="r.return_sn"
        class="rounded-md border border-n-weak p-2 flex flex-col gap-1.5"
      >
        <div class="flex items-center gap-2 text-xs">
          <span
            class="px-1.5 py-0.5 rounded font-medium"
            :class="statusClass(r.status)"
          >
            {{ r.status }}
          </span>
          <span class="font-mono text-n-slate-11">{{ r.return_sn }}</span>
          <span v-if="fmtAmount(r)" class="font-semibold text-n-slate-12">
            {{ fmtAmount(r) }}
          </span>
          <span v-if="fmtDue(r)" class="ml-auto text-y-warning-11">
            {{ fmtDue(r) }}
          </span>
        </div>
        <div class="text-xs text-n-slate-11">
          <span class="font-medium">{{ r.reason }}</span>
          <span v-if="r.text_reason"> — {{ r.text_reason.slice(0, 140) }}</span>
        </div>

        <div
          v-if="canApprove(r) || canReject(r)"
          class="flex items-center gap-2 pt-0.5"
        >
          <button
            v-if="canApprove(r)"
            class="px-2.5 py-1 rounded-md text-xs font-medium bg-g-success-9 text-white hover:bg-g-success-10 disabled:opacity-50"
            :disabled="busySn === r.return_sn"
            @click="approve(r)"
          >
            ✓ Approve refund
          </button>
          <button
            v-if="canReject(r)"
            class="px-2.5 py-1 rounded-md text-xs font-medium bg-r-alert-9 text-white hover:bg-r-alert-10 disabled:opacity-50"
            :disabled="busySn === r.return_sn"
            @click="
              rejectOpenFor === r.return_sn
                ? (rejectOpenFor = '')
                : openReject(r)
            "
          >
            ✕ Reject (dispute)
          </button>
          <span v-if="busySn === r.return_sn" class="text-xs text-n-slate-10">
            working…
          </span>
        </div>

        <div
          v-if="rejectOpenFor === r.return_sn"
          class="flex flex-col gap-1.5 rounded-md bg-n-alpha-1 p-2"
        >
          <div class="text-[11px] text-n-slate-11">
            Rejecting raises a dispute — Shopee arbitrates. Provide a contact
            email and reason.
          </div>
          <select
            v-model="rejectForm.reason"
            class="text-xs rounded border border-n-weak bg-n-background px-2 py-1"
          >
            <option value="" disabled>Select dispute reason…</option>
            <option
              v-for="dr in disputeReasons"
              :key="dr.reason_id || dr.dispute_reason"
              :value="dr.reason_id || dr.dispute_reason"
            >
              {{ dr.reason_text || dr.text || dr.reason }}
            </option>
          </select>
          <input
            v-model="rejectForm.email"
            type="email"
            placeholder="Contact email (required)"
            class="text-xs rounded border border-n-weak bg-n-background px-2 py-1"
          />
          <textarea
            v-model="rejectForm.text"
            rows="2"
            placeholder="Explanation (optional)"
            class="text-xs rounded border border-n-weak bg-n-background px-2 py-1"
          />
          <div class="flex gap-2">
            <button
              class="px-2.5 py-1 rounded-md text-xs font-medium bg-r-alert-9 text-white hover:bg-r-alert-10 disabled:opacity-50"
              :disabled="
                !rejectForm.reason ||
                !rejectForm.email ||
                busySn === r.return_sn
              "
              @click="submitReject(r)"
            >
              Submit dispute
            </button>
            <button
              class="px-2.5 py-1 rounded-md text-xs text-n-slate-11 hover:bg-n-alpha-2"
              @click="rejectOpenFor = ''"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div v-if="actionError" class="text-xs text-r-alert-11">
        {{ actionError }}
      </div>
    </div>
  </div>
</template>
